"use client"

import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import { agentService, Agent, CreateAgentRequest, UpdateAgentRequest } from "@/services/api"
import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import { useLanguage } from "@/contexts/LanguageContext"
import { 
  ArrowLeft, 
  Save, 
  Play, 
  Pause, 
  Trash2, 
  Settings, 
  Bot, 
  MessageSquare,
  FileText,
  Image,
  Code,
  Activity,
  Clock,
  Tag,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
  ChevronDown,
  ChevronRight
} from "lucide-react"
import MarkdownRenderer from "@/components/MarkdownRenderer"

export default function AgentDetailPage() {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  
  const [agent, setAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [agentStatus, setAgentStatus] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)
  const [showRebuildConfirm, setShowRebuildConfirm] = useState(false)
  const [agentStatusHistory, setAgentStatusHistory] = useState<any[]>([])
  const [pendingChanges, setPendingChanges] = useState<Partial<Agent> | null>(null)
  
  // Form state
  const [formData, setFormData] = useState<Partial<Agent>>({
    name: '',
    description: '',
    agentType: 'conversation',
    status: 'draft',
    tags: []
  })
  
  const [newTag, setNewTag] = useState('')
  // 计算等待时长
  const getWaitingDuration = () => {
    if (!agent) return null
    
    // 优先使用 gmtModified，如果不存在则使用 gmtCreated
    const startTime = agent.gmtModified || agent.gmtCreated
    if (!startTime) return null
    
    const now = Date.now()
    const duration = now - startTime
    const minutes = Math.floor(duration / (1000 * 60))
    const seconds = Math.floor((duration % (1000 * 60)) / 1000)
    
    console.log('Waiting duration calculation:', {
      agentId: agent.id,
      agentStatus: agent.agentStatus,
      gmtModified: agent.gmtModified,
      gmtCreated: agent.gmtCreated,
      startTime,
      now,
      duration,
      result: `${minutes}m ${seconds}s`
    })
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`
    }
    return `${seconds}s`
  }

  // 状态刷新定时器
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null

    // 如果agent状态为QUEUED、BUILDING或READY，启动定时刷新
    if (agent ) {
      let checkFn = async () => {
        const statusResponse = await agentService.getAgentStatus(id!)
        setAgentStatusHistory(statusResponse.lifeCycleEvents)
        setAgentStatus(statusResponse.currentStatus)
      }
      intervalId = setInterval(checkFn, 10000)
      checkFn()
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [agentStatus, id])

  // 等待时长显示更新定时器
  useEffect(() => {
    let durationIntervalId: NodeJS.Timeout | null = null

    // 如果agent状态为QUEUED、READY且有开始时间，每秒更新显示
    if (['QUEUED', 'READY','PROCESSING'].includes(agentStatus || '') && (agent?.gmtModified || agent?.gmtCreated)) {
      durationIntervalId = setInterval(() => {
        // 强制重新渲染以更新等待时长显示
        setAgent(prev => prev ? { ...prev } : null)
      }, 1000)
    }

    return () => {
      if (durationIntervalId) {
        clearInterval(durationIntervalId)
      }
    }
  }, [agentStatus, agent?.gmtModified, agent?.gmtCreated])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        
        if (id) {
          // 获取现有agent详情
          const agentData = await agentService.getAgent(id)
          setAgent(agentData)
          setAgentStatus(agentData.agentStatus)
          //DEBUG
          // 处理状态映射：'NORMAL' 映射到 'active'
          
          setFormData({
            name: agentData.name,
            description: agentData.description,
            agentType: agentData.agentType as 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant',
            status: agentData.agentStatus,
            tags: agentData.tags || [],
            prompt: agentData.prompt
          })
        } else {
          // 新建agent，设置默认值
          setFormData({
            name: '',
            description: '',
            agentType: 'conversation',
            status: 'draft',
            tags: []
          })
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          toast.error(t('agentDetail.agentNotFound'))
          navigate("/dashboard")
        } else {
          toast.error(t('agentDetail.failedLoadData'))
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, navigate])

  const handleSave = async () => {
    if (!formData.name?.trim() || !formData.description?.trim()) {
      toast.error(t('agentDetail.fillRequiredFields'))
      return
    }

    // 检查是否需要重建
    const needsRebuild = (formData.agentType !== agent?.agentType) || (formData.prompt !== agent?.prompt)
    
    if (needsRebuild) {
      // 需要重建，显示确认对话框
      setPendingChanges(formData)
      setShowRebuildConfirm(true)
      return
    }

    try{
      setSaving(true)
      
        // 更新现有agent
        const updateData: UpdateAgentRequest = {
          name: formData.name,
          description: formData.description,
          status: formData.status === 'NORMAL' ? 'active' : formData.status,
          tags: formData.tags,
          agentType: (formData.agentType as 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant') || undefined,
        }
        
        const updatedAgent = await agentService.updateAgentItem(id!, updateData, false)
        setAgent(updatedAgent)
        
        // 使用返回的最新数据更新表单
        const mappedStatus = (updatedAgent.status === 'NORMAL' ? 'active' : updatedAgent.status) as 'active' | 'inactive' | 'draft' | 'error'
        setFormData({
          name: updatedAgent.name,
          description: updatedAgent.description,
          agentType: updatedAgent.agentType as 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant',
          status: mappedStatus,
          tags: updatedAgent.tags || [],
          prompt: updatedAgent.prompt
        })
        
        toast.success(t('agentDetail.agentUpdated'))
        
        // 退出编辑模式
        setIsEditing(false)
        setShowAdvancedSettings(false)
      
    } catch (error: any) {
      toast.error(t('agentDetail.failedSave'))
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = () => {
    // 重置表单数据为当前agent的数据
    if (agent) {
      const mappedStatus = (agent.status === 'NORMAL' ? 'active' : agent.status) as 'active' | 'inactive' | 'draft' | 'error'
      setFormData({
        name: agent.name,
        description: agent.description,
        agentType: agent.agentType as 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant',
        status: mappedStatus,
        tags: agent.tags || [],
        prompt: agent.prompt
      })
    }
    setIsEditing(false)
    setShowAdvancedSettings(false)
  }

  const handleAdvancedSettingChange = (field: string, value: any) => {
    const newFormData = { ...formData, [field]: value }
    setFormData(newFormData)
  }

  const handleRebuildConfirm = async () => {
    if (!pendingChanges) return
    
    try {
      setSaving(true)
      setShowRebuildConfirm(false)
      
      const updateData: UpdateAgentRequest = {
        name: pendingChanges.name,
        description: pendingChanges.description,
        status: pendingChanges.status === 'NORMAL' ? 'active' : pendingChanges.status,
        tags: pendingChanges.tags,
        agentType: (pendingChanges.agentType as 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant') || undefined,
        prompt: pendingChanges.prompt || undefined,
      }
      
      const updatedAgent = await agentService.updateAgentItem(id!, updateData, true)
      setAgent(updatedAgent)
      
      // 使用返回的最新数据更新表单
      const mappedStatus = (updatedAgent.status === 'NORMAL' ? 'active' : updatedAgent.status) as 'active' | 'inactive' | 'draft' | 'error'
      setFormData({
        name: updatedAgent.name,
        description: updatedAgent.description,
        agentType: updatedAgent.agentType as 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant',
        status: mappedStatus,
        tags: updatedAgent.tags || [],
        prompt: updatedAgent.prompt
      })
      
      setPendingChanges(null)
      
      toast.success('Agent updated and will be rebuilt. This may take a few minutes.')
      
      // 退出编辑模式
      setIsEditing(false)
      setShowAdvancedSettings(false)
    } catch (error: any) {
      toast.error(t('agentDetail.failedSave'))
    } finally {
      setSaving(false)
    }
  }

  const handleRebuildCancel = () => {
    setShowRebuildConfirm(false)
    setPendingChanges(null)
  }

  const handleDelete = async () => {
    if (!agent) return
    
    if (window.confirm(t('agentDetail.deleteConfirm').replace('{name}', agent.name))) {
      try {
        await agentService.deleteAgent(agent.id)
        toast.success(t('agentDetail.agentDeleted'))
        navigate("/dashboard")
      } catch (error: any) {
        toast.error(t('agentDetail.failedDelete'))
      }
    }
  }

  const handleToggleStatus = async () => {
    if (!agent) return
    
    try {
      const newStatus = agent.status === 'active' ? 'inactive' : 'active'
      const updatedAgent = await agentService.toggleAgentStatus(agent.id, newStatus)
      setAgent(updatedAgent)
      setFormData(prev => ({ ...prev, status: newStatus }))
      toast.success(newStatus === 'active' ? t('agentDetail.agentActivated') : t('agentDetail.agentDeactivated'))
    } catch (error: any) {
      toast.error(t('agentDetail.failedUpdateStatus'))
    }
  }

  const addTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }))
  }

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'QUEUED':
        return <Clock className="h-4 w-4 text-blue-500" />
      case 'PROCESSING':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
      case 'RUNNING':
        return <Loader2 className="h-4 w-4 animate-spin" />
      case 'READY':
        return <Loader2 className="h-4 w-4 text-green-500 animate-spin" />
      case 'COMPLETE':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'FAILED':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      case 'inactive':
        return <XCircle className="h-4 w-4 text-gray-500" />
      case 'draft':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />
    }
  }

  const getAgentStatusColor = (status: string) => {
    switch (status) {
      case 'QUEUED':
        return 'border-blue-200 bg-blue-50'
      case 'BUILDING':
        return 'border-blue-200 bg-blue-50'
      case 'READY':
        return 'border-blue-200 bg-blue-50'
      case 'COMPLETE':
        return 'border-green-200 bg-green-50'
      case 'FAILED':
        return 'border-red-200 bg-red-200'
      case 'PROCESSING':
        return 'border-blue-200 bg-blue-200'
      default:
        return 'border-blue-200 bg-blue-100'
    }
  }
  const getAgentStatusSubtitle = (status: string) => {
    switch (status) {
      case 'QUEUED':
        return 'Agent 正在等待构建。'
      case 'PROCESSING':
        return 'Agent 正在构建中。'
      case 'READY':
        return 'Agent 已准备就绪，即将开始开发。' 
      case 'COMPLETE':
        return 'Agent 构建完成。'
      case 'FAILED':
        return 'Agent 构建失败。'
      default:
        return 'Agent 状态未知。' 
    }
  }

  const getAgentStatusDescription = (status:string) => {
    const icon = getStatusIcon(status)
    let msg = ""
    const item = agentStatusHistory.sort((a, b) => new Date(b.gmtModified).getTime() - new Date(a.gmtModified).getTime()).find(item => item.agentStatus === status)
    console.log(item,agentStatusHistory,status)
    if (item) {
        msg = <MarkdownRenderer content={item.message} agentId={agent?.id} />
    }else{
      switch (status) {
        case 'QUEUED':
          msg = 'Your agent is waiting in the build queue. It will start building soon.';
          break;
        case 'PROCESSING':
          msg = 'Agent 正在构建中。'
          break;
        case 'READY':
          msg = 'Your agent has been prepared and is ready to start development. The development process will begin shortly.';
          break;
        case 'COMPLETE':
          msg = 'Agent 构建完成。'
          break;
        case 'FAILED':
          msg = 'Agent 构建失败。'
          break;
        default:
          msg = 'Agent 状态未知。'
          break;
      }
    }
    return <div className="flex items-center space-x-2">
      {icon}
      <span>{msg}</span>
    </div>
  }

  const getLabel = (status: string) => {
    switch (status) {
      case 'QUEUED':
        return <span className="text-blue-500 font-bold text-sm">{status}</span>
      case 'READY':
        return <span className="text-green-500 font-bold text-sm">{status}</span>
      case 'PROCESSING':
        return <span className="text-blue-500 font-bold text-sm">{status}</span>
      case 'COMPLETE':
        return <span className="text-green-500 font-bold text-sm">{status}</span>
      case 'FAILED':
        return <span className="text-red-500 font-bold text-sm">{status}</span>
      default:
        return <span className="text-gray-500 font-bold text-sm">{status}</span>
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'conversation':
        return <MessageSquare className="h-4 w-4" />
      case 'file_conversion':
        return <FileText className="h-4 w-4" />
      case 'image_generation':
        return <Image className="h-4 w-4" />
      case 'code_assistant':
        return <Code className="h-4 w-4" />
      default:
        return <Bot className="h-4 w-4" />
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20 pb-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
              <div className="space-y-6">
                <div className="h-64 bg-gray-200 rounded"></div>
                <div className="h-64 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/dashboard")}
                  className="flex items-center space-x-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>{t('agentDetail.backToDashboard')}</span>
                </Button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {agent?.name || t('agentDetail.agentDetails')}
                  </h1>
                  <p className="text-gray-600 mt-1">
                    {t('agentDetail.editAgentConfig')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          
          {/* Main Content */}
          <div className="space-y-6">
            {/* Basic Info Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('agentDetail.basicInformation')}</CardTitle>
                    <CardDescription>
                      {t('agentDetail.basicInfoDesc')}
                    </CardDescription>
                  </div>
                  {
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => isEditing ? handleCancelEdit() : setIsEditing(true)}
                      className="flex items-center space-x-2"
                    >
                      {isEditing ? (
                        <>
                          <XCircle className="h-4 w-4" />
                          <span>Cancel</span>
                        </>
                      ) : (
                        <>
                          <Settings className="h-4 w-4" />
                          <span>Edit</span>
                        </>
                      )}
                    </Button>
                  }
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  // 编辑模式或新建模式 - 显示表单
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t('agentDetail.name')} *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder={t('agentDetail.namePlaceholder')}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="type">{t('agentDetail.type')} *</Label>
                        <Select
                          value={formData.agentType || 'conversation'}
                          onValueChange={(value: any) => handleAdvancedSettingChange('agentType', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={t('agentDetail.selectAgentType')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="conversation">
                              <div className="flex items-center space-x-2">
                                <MessageSquare className="h-4 w-4" />
                                <span>{t('agent.type.conversation')}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="file_conversion">
                              <div className="flex items-center space-x-2">
                                <FileText className="h-4 w-4" />
                                <span>{t('agent.type.fileConversion')}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="image_generation">
                              <div className="flex items-center space-x-2">
                                <Image className="h-4 w-4" />
                                <span>{t('agent.type.imageGeneration')}</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="code_assistant">
                              <div className="flex items-center space-x-2">
                                <Code className="h-4 w-4" />
                                <span>{t('agent.type.codeAssistant')}</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">{t('agentDetail.description')} *</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder={t('agentDetail.descriptionPlaceholder')}
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>{t('agentDetail.tags')}</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.tags?.map((tag, index) => (
                          <Badge key={index} variant="secondary" className="flex items-center space-x-1">
                            <span>{tag}</span>
                            <button
                              onClick={() => removeTag(tag)}
                              className="ml-1 hover:text-red-500"
                            >
                              <XCircle className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex space-x-2">
                        <Input
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          placeholder={t('agentDetail.addTag')}
                          onKeyPress={(e) => e.key === 'Enter' && addTag()}
                        />
                        <Button variant="outline" onClick={addTag}>
                          {t('agentDetail.add')}
                        </Button>
                      </div>
                    </div>
                    
                    {/* 高级设置 */}
                    <div className="space-y-4 pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                        className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                      >
                        {showAdvancedSettings ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        <span>Advanced Settings</span>
                      </button>
                      
                      {showAdvancedSettings && (
                        <div className="space-y-4 pl-6 border-l-2 border-gray-200">
                          <div className="space-y-2">
                            <Label htmlFor="prompt">Prompt</Label>
                            <Textarea
                              id="prompt"
                              value={formData.prompt || ''}
                              onChange={(e) => handleAdvancedSettingChange('prompt', e.target.value)}
                              placeholder="Enter the prompt for your agent..."
                              rows={6}
                              className="font-mono text-sm"
                            />
                            <p className="text-xs text-gray-500">
                              Changing the prompt will trigger agent rebuilding. This may take a few minutes.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* 编辑模式下的保存按钮 */}
                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center space-x-2"
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        <span>{saving ? t('agentDetail.saving') : t('agentDetail.save')}</span>
                      </Button>
                    </div>
                  </>
                ) : (
                  // 只读模式 - 显示当前值
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">{t('agentDetail.name')}</h3>
                        <p className="text-lg text-gray-900">{agent?.name || '-'}</p>
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 mb-2">{t('agentDetail.type')}</h3>
                        <div className="flex items-center space-x-2">
                          {getTypeIcon(agent?.agentType || 'conversation')}
                          <span className="text-lg text-gray-900">
                            {agent?.agentType === 'conversation' && t('agent.type.conversation')}
                            {agent?.agentType === 'file_conversion' && t('agent.type.fileConversion')}
                            {agent?.agentType === 'image_generation' && t('agent.type.imageGeneration')}
                            {agent?.agentType === 'code_assistant' && t('agent.type.codeAssistant')}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">{t('agentDetail.description')}</h3>
                      <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{agent?.description || '-'}</p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">{t('agentDetail.tags')}</h3>
                      {agent?.tags && agent.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {agent.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">No tags</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Building Status Module */}
            {agentStatus && (
            <Card className={`mb-6 ${getAgentStatusColor(agentStatus)}`}>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-blue-800">
                  <span>Agent 构建进度</span>{getLabel(agentStatus)}
                </CardTitle>
                <CardDescription className="text-blue-600">
                  {agentStatus}: {getAgentStatusSubtitle(agentStatus)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">    
                  <div className="bg-blue-100 rounded-lg p-3">
                    <div className="flex items-center space-x-2 text-sm text-blue-700">
                      <span>
                        {getAgentStatusDescription(agentStatus)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

            {/* Action Buttons */}
            {!isEditing && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {agent && (
                        <>
                          <Button
                            variant="outline"
                            onClick={handleToggleStatus}
                            className="flex items-center space-x-2"
                          >
                            {agent.status === 'active' ? (
                              <>
                                <Pause className="h-4 w-4" />
                                <span>{t('agentDetail.deactivate')}</span>
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4" />
                                <span>{t('agentDetail.activate')}</span>
                              </>
                            )}
                          </Button>
                          
                          <Button
                            variant="outline"
                            onClick={handleDelete}
                            className="flex items-center space-x-2 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>{t('agentDetail.delete')}</span>
                          </Button>
                        </>
                      )}
                    </div>
                    
                    {
                      <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center space-x-2"
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        <span>{saving ? t('agentDetail.saving') : t('agentDetail.save')}</span>
                      </Button>
                    }
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
        </div>
      </div>

      <Footer />
      
      {/* 重新构建确认对话框 */}
      {showRebuildConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900">Confirm Agent Rebuild</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {pendingChanges?.agentType !== agent?.agentType && pendingChanges?.prompt !== agent?.prompt 
                ? 'Changing the agent type and prompt will trigger agent rebuilding. This process may take a few minutes and the agent will be temporarily unavailable.'
                : pendingChanges?.agentType !== agent?.agentType
                ? 'Changing the agent type will trigger agent rebuilding. This process may take a few minutes and the agent will be temporarily unavailable.'
                : 'Changing the prompt will trigger agent rebuilding. This process may take a few minutes and the agent will be temporarily unavailable.'
              }
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={handleRebuildCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRebuildConfirm}
                disabled={saving}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Updating...
                  </>
                ) : (
                  'Continue & Rebuild'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 